import axios, {AxiosInstance} from 'axios'
import MyAxiosInstanceDocument from './axiosInstanceDocument'
import {Question} from '../types/question'
import {project} from '../types/project'
import {Project} from '../types/getDocuments'
import {UploadedDocumentContent} from '../types/question'
import {userDocumentsInfo} from '../types/userDocumentsInfo'
import {FlowDocumentType, ProjectFlowOption, ProjectType} from '../types/flowData'

export type DocumentQuestionUploadStatus = 'uploading' | 'uploaded'

export type TryGetAnswersResponse = {
    dispatched: boolean
    documents: number
    questions: number
    reason: string | null
}

type QuestionPreSignedUrlResponse = {
    url: string
    tagging: string
    document_id: string
    document_name: string
    filepath: string
    status: 'waiting_upload' | 'uploaded' | 'processing' | 'processed' | 'failed'
}

export class DocumentApi {
    private documentApi: AxiosInstance

    client = axios.create({
        baseURL: import.meta.env.VITE_DOCUMENT_URL,
    })

    constructor() {
        this.documentApi = MyAxiosInstanceDocument
    }

    // sem redirect para /Erro: chamado no menu lateral, em toda página
    async getOrganizationProjectTypes(): Promise<ProjectType[]> {
        const res = await this.documentApi.get('/document-flows/project-types')
        return res.data
    }

    // flows da org do usuário para o project type
    async getProjectTypeFlows(projectTypeId: string): Promise<ProjectFlowOption[]> {
        const res = await this.documentApi.get('/document-flows', {
            params: {project_type_id: projectTypeId},
        })
        return res.data
    }

    async getProjectTypeDocumentTypes(
        projectTypeId: string,
        documentFlowId?: string,
        organizationUuid?: string
    ): Promise<FlowDocumentType[]> {
        // axios omite params undefined
        const res = await this.documentApi.get(
            `/document-flows/project-types/${projectTypeId}/document-types`,
            {params: {document_flow_id: documentFlowId, organization_uuid: organizationUuid}}
        )
        return res.data
    }

    async getResume(project_id: string): Promise<any> {
        try {
            const res = await this.documentApi.get(
                `/projects/${project_id}/answers`
            )
            return res.data
        } catch (error) {
            window.location.href = '/Erro'
            throw error
        }
    }

    async postAnswer(answer: Record<string, any>): Promise<Question> {
        try {
            const res = await this.documentApi.post(`/projects/answer`, answer)
            return res.data
        } catch (error) {
            window.location.href = '/Erro'
            console.log(error)
            throw error
        }
    }

    async uploadQuestionDocument(
        projectId: string,
        questionId: string,
        file: File,
        onStatusChange?: (status: DocumentQuestionUploadStatus) => void
    ): Promise<UploadedDocumentContent> {
        try {
            const uploadData = await this.postQuestionPreSignedUrl(
                projectId,
                questionId,
                file
            )
            const contentType = file.type || 'application/octet-stream'

            onStatusChange?.('uploading')
            await axios.put(uploadData.url, file, {
                headers: {
                    'Content-Type': contentType,
                    'x-amz-tagging': uploadData.tagging,
                },
            })

            onStatusChange?.('uploaded')
            return {
                document_id: uploadData.document_id,
                document_name: uploadData.document_name,
                content: null,
                filepath: uploadData.filepath,
                status: uploadData.status,
                error: null,
            }
        } catch (error) {
            console.error('Erro ao enviar documento da pergunta:', error)
            throw error
        }
    }

    async getPreviousQuestion(
        project_id: string,
        question_id: string,
        answer: any
    ): Promise<Question> {
        try {
            const res = await this.documentApi.post(
                `/projects/${project_id}/previous-question/${question_id}`,
                answer
            )

            return res.data
        } catch (error: any) {
            window.location.href = '/Erro'
            throw error
        }
    }

    async getNextQuestion(project_id: string): Promise<Question> {
        try {
            const res = await this.documentApi.get(
                `/projects/${project_id}/next-question`
            )

            return res.data
        } catch (error: any) {
            window.location.href = '/Erro'
            throw error
        }
    }

    async finishProject(project_id: string): Promise<void> {
        try {
            await this.documentApi.post(`/projects/${project_id}/finish`)
            return
        } catch (error) {
            window.location.href = '/Erro'
            throw error
        }
    }

    async getProjects(limit = 15, skip = 0, projectTypeId?: string): Promise<project[]> {
        try {
            const typeFilter = projectTypeId ? `&project_type_id=${projectTypeId}` : ''
            const res: {data: project[]} = await this.documentApi.get(
                `/projects?limit=${limit}&skip=${skip}${typeFilter}`
            )
            return res.data
        } catch (error) {
            console.error(error)
            window.location.href = '/Erro'
            throw error
        }
    }

    async getProjectById(project_id: string): Promise<project> {
        try {
            const res = await this.documentApi.get(`/projects/${project_id}`)
            return res.data
        } catch (error) {
            console.error(error)
            throw new Error('Erro ao obter documentos da API.')
        }
    }

    async postProject(projectData: {
        name: string
        project_type_id: string
        document_types: string[]
        document_flow_id?: string
    }): Promise<Question> {
        try {
            const response = await this.documentApi.post(
                '/projects',
                projectData
            )
            return response.data
        } catch (error) {
            console.error('Erro criando projeto:', error)
            if (axios.isAxiosError(error) && error.response) {
                window.location.href = '/Erro'
                throw new Error(
                    `Erro ao criar projeto: ${
                        error.response.data.message || error.response.statusText
                    }`
                )
            } else {
                window.location.href = '/Erro'
                throw new Error('Erro ao criar projeto: Erro desconhecido')
            }
        }
    }

    async archiveProject(project_guid: string): Promise<void> {
        try {
            await this.documentApi.delete(`/projects/${project_guid}/archive`)
            return
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async unarchiveProject(project_guid: string): Promise<void> {
        try {
            await this.documentApi.put(`/projects/${project_guid}/restore`)
            return
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async getArchivedProjects(limit = 15, skip = 0): Promise<project[]> {
        try {
            const res = await this.documentApi.get(
                `/projects/archived?limit=${limit}&skip=${skip}`
            )
            return res.data
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async getUserInfo(): Promise<userDocumentsInfo> {
        try {
            const res = await this.documentApi.get('/users')
            return res.data
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async updateProject(projectData: {
        project_id: string
        document_types: string[]
    }) {
        try {
            const response = await this.documentApi.put(
                `/projects/${projectData.project_id}`,
                {
                    documents: projectData.document_types,
                }
            )
            return response.data
        } catch (error) {
            console.error('Erro atualizando projeto:', error)
            if (axios.isAxiosError(error) && error.response) {
                throw new Error(
                    `Erro ao atualizar projeto: ${
                        error.response.data.message || error.response.statusText
                    }`
                )
            } else {
                throw new Error('Erro ao atualizar projeto: Erro desconhecido')
            }
        }
    }

    async getQuestionById(
        project_id: string,
        question_id: string
    ): Promise<Question> {
        try {
            const res = await this.documentApi.get(
                `/projects/${project_id}/question/${question_id}`
            )
            return res.data
        } catch (error) {
            console.error('Erro ao obter pergunta:', error)
            throw error
        }
    }

    async downloadAllDocuments(project_id: string): Promise<void> {
        try {
            const response = await this.documentApi.get(
                `/projects/${project_id}/download-all`,
                {responseType: 'blob'}
            )

            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `project-${project_id}-documents.zip`)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Erro ao baixar documentos:', error)
            throw error
        }
    }

    async deleteDocument(
        project_id: string,
        document_id: string
    ): Promise<void> {
        try {
            await this.documentApi.delete(
                `/projects/${project_id}/document/${document_id}`
            )
        } catch (error) {
            console.error('Erro ao deletar documento:', error)
            throw error
        }
    }

    // LCTO-1614: `includeInvisible` traz as dependências que o fluxo criou
    // sozinho (`is_visible: false`), que por padrão o backend não devolve.
    // Só a pergunta de upload precisa delas — dependência invisível é o único
    // documento que um arquivo enviado pode substituir.
    async getDocuments(
        project_id: string,
        includeInvisible = false
    ): Promise<Project> {
        try {
            const res = await this.documentApi.get(`/projects/${project_id}`, {
                params: includeInvisible ? {include_invisible: true} : undefined,
            })
            return res.data
        } catch (error) {
            console.error('Erro ao obter documentos:', error)
            throw error
        }
    }

    async getProjectQuestions(): Promise<any> {
        try {
            const res = await this.documentApi.get(
                `/projects/types/899704fd-10be-4aac-8b36-cafccc3f0f0a/questions`
            )
            return res.data
        } catch (error) {
            console.error('Erro ao obter perguntas do projeto:', error)
            throw error
        }
    }
    async retryDocumentProcessing(
        project_id: string,
        document_id: string
    ): Promise<void> {
        try {
            await this.documentApi.put(`/projects/${project_id}/retry`, {
                documents: [document_id],
            })
        } catch (error) {
            console.error('Erro ao reenviar processamento do documento:', error)
            throw error
        }
    }

    async downloadDocument(
        project_id: string,
        document_id: string,
        document_name: string
    ): Promise<void> {
        try {
            const response = await this.documentApi.get(
                `/projects/${project_id}/${document_id}/download`,
                {responseType: 'blob'}
            )

            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `${document_name}.docx`)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Erro ao baixar documento:', error)
            throw error
        }
    }

    async cloneProject(project_id: string, body: any): Promise<project> {
        try {
            const res = await this.documentApi.post(
                `/projects/clone/${project_id}`,
                body
            )
            return res.data
        } catch (error) {
            console.error('Erro ao clonar projeto:', error)
            throw error
        }
    }

    async postQuestionPreSignedUrl(
        projectId: string,
        questionId: string,
        file: File
    ): Promise<QuestionPreSignedUrlResponse> {
        try {
            const res = await this.documentApi.post(
                `/projects/${projectId}/questions/${questionId}/document-upload/presigned-url`,
                {
                    file_name: file.name,
                    content_type: file.type || 'application/octet-stream',
                    file_size: file.size,
                }
            )
            return res.data
        } catch (error) {
            console.error('Erro ao obter URL pré-assinada para upload:', error)
            throw error
        }
    }

    /** Pede ao backend que tente responder o fluxo a partir dos arquivos enviados
     * nesta pergunta. Atalho, nao pre-requisito: o despacho e assincrono (SQS +
     * callback) e a resposta so diz se saiu. */
    async tryGetAnswers(
        projectId: string,
        questionId: string
    ): Promise<TryGetAnswersResponse> {
        const res = await this.documentApi.post(
            `/projects/${projectId}/questions/${questionId}/try-get-answers`
        )
        return res.data
    }

    async draftAnswers(answer: any): Promise<void> {
        try {
            await this.documentApi.post('answers/draft', answer)
        } catch (error) {
            console.error('Erro ao salvar rascunho da resposta:', error)     
        }
    }
}
